import { InputRule } from "prosemirror-inputrules";
import type { EditorState } from "prosemirror-state";

const ruleOptions = { inCodeMark: false } as const;

/**
 * Text of the current block up to the caret. Used by the quote rules: a line
 * holding a backtick is probably code being quoted, and the spec leaves it
 * alone (`editor.md` §7).
 */
function textBefore(state: EditorState, pos: number): string {
  const $pos = state.doc.resolve(pos);
  return $pos.parent.textBetween(0, Math.max(0, pos - $pos.start()), "\n", "￼");
}

function replaceRule(
  match: RegExp,
  replacement: string,
  options: { guardBackticks?: boolean } = {},
): InputRule {
  return new InputRule(
    match,
    (state, found, start, end) => {
      if (options.guardBackticks === true && textBefore(state, start).includes("`")) return null;
      // Group 1, when present, is the part to replace; the rest is context.
      const target = found[1] ?? found[0];
      const offset = found[0].lastIndexOf(target);
      return state.tr.insertText(replacement, start + offset, end);
    },
    ruleOptions,
  );
}

/**
 * §7 smart-text substitutions. These are an **editor** behavior only: the
 * markdown parser never applies them, which is why they live here and not in
 * `@wiki/core`.
 */
export function buildSmartTextRules(): InputRule[] {
  return [
    replaceRule(/->$/, "→"),
    // `--` becomes an em dash unless it follows a pipe, where it is a table rule.
    replaceRule(/(?:^|[^|])(-- )$/, "— "),
    replaceRule(/(?:^|[^\d/])(1\/2)$/, "½"),
    replaceRule(/(?:^|[^\d/])(3\/4)$/, "¾"),
    replaceRule(/\((?:c|C)\)$/, "©"),
    replaceRule(/\((?:r|R)\)$/, "®"),
    replaceRule(/\((?:tm|TM)\)$/, "™"),
    replaceRule(/\.\.\.$/, "…"),
    replaceRule(/(?:^|[\s{[(<'"‘“])(")$/, "“"),
    replaceRule(/(")$/, "”", { guardBackticks: true }),
    replaceRule(/(?:^|[\s{[(<'"‘“])(')$/, "‘"),
    replaceRule(/(')$/, "’", { guardBackticks: true }),
  ];
}
